"use client";

import { Button } from "@carbon/react";
import Link from "next/link";
import { LandingCarousel } from "@/components/landing/LandingCarousel";
import { WaitlistForm } from "@/components/landing/WaitlistForm";
import {
  ChatProductMock,
  CompareProductMock,
  ReviseProductMock,
  VersionsProductMock,
  WikiProductMock,
  WikiRelationsProductMock,
} from "@/components/landing/ProductMocks";
import {
  landingAgentSlides,
  landingCopy,
  landingCraft,
  landingDeploy,
  landingNav,
  landingPain,
  landingProduct,
  landingScenarios,
  landingSteps,
  landingTrust,
  landingWikiSlides,
  waitlistHref,
} from "@/lib/ui/landing";

export function LandingView() {
  return (
    <div className="landing">
      <header className="landing-nav">
        <Link className="landing-brand" href="/">
          {landingCopy.brand}
        </Link>
        <nav className="landing-nav-links" aria-label="Разделы">
          {landingNav.map((item) => (
            <Link key={item.href} href={item.href}>
              {item.label}
            </Link>
          ))}
        </nav>
        <nav className="landing-nav-actions">
          <Button as={Link} href="/login" kind="ghost" size="md">
            {landingCopy.login}
          </Button>
          <Button as={Link} href={waitlistHref()} kind="primary" size="md">
            {landingCopy.cta}
          </Button>
        </nav>
      </header>

      <main>
        <section className="landing-hero">
          <p className="landing-kicker">{landingCopy.heroEyebrow}</p>
          <h1>
            Знания компании,
            <br />
            которым можно <em>верить</em>
          </h1>
          <p className="landing-lead">{landingCopy.heroLead}</p>
          <div className="landing-hero-actions">
            <Button as={Link} href={waitlistHref()} kind="primary" size="md">
              {landingCopy.cta}
            </Button>
            <Button as={Link} href="#product" kind="tertiary" size="md">
              {landingCopy.ctaSecondary}
            </Button>
          </div>
          <ul className="landing-trust">
            {landingTrust.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-stage">
          <ChatProductMock />
        </section>

        <section className="landing-section">
          <header className="landing-section-head">
            <h2>{landingCopy.painTitle}</h2>
            <p className="landing-lead">{landingCopy.painLead}</p>
          </header>
          <ul className="landing-scenarios">
            {landingPain.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-section" id="product">
          <header className="landing-section-head">
            <h2>{landingCopy.productTitle}</h2>
            <p className="landing-lead">{landingCopy.productLead}</p>
          </header>
          <ul className="landing-scenarios">
            {landingProduct.map((item) => (
              <li key={item.id}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-section">
          <header className="landing-section-head">
            <h2>{landingCopy.stepsTitle}</h2>
            <p className="landing-lead">{landingCopy.stepsLead}</p>
          </header>
          <ol className="landing-steps">
            {landingSteps.map((step) => (
              <li key={step.n}>
                <span>{step.n}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <LandingCarousel
          title={landingCopy.wikiSlidesTitle}
          lead={landingCopy.wikiSlidesLead}
          label={landingCopy.wikiSlidesTitle}
          slides={[
            {
              ...landingWikiSlides[0],
              stage: <WikiProductMock />,
            },
            {
              ...landingWikiSlides[1],
              stage: <VersionsProductMock />,
            },
            {
              ...landingWikiSlides[2],
              stage: <WikiRelationsProductMock />,
            },
          ]}
        />

        <LandingCarousel
          title={landingCopy.agentSlidesTitle}
          lead={landingCopy.agentSlidesLead}
          label={landingCopy.agentSlidesTitle}
          slides={[
            {
              ...landingAgentSlides[0],
              stage: <CompareProductMock />,
            },
            {
              ...landingAgentSlides[1],
              stage: <ReviseProductMock />,
            },
          ]}
        />

        <section className="landing-section">
          <header className="landing-section-head">
            <h2>{landingCopy.craftTitle}</h2>
            <p className="landing-lead">{landingCopy.craftLead}</p>
          </header>
          <ol className="landing-craft">
            {landingCraft.map((item) => (
              <li key={item.n}>
                <span>{item.n}</span>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.problem}</p>
                  <p className="landing-craft-gain">{item.gain}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="landing-section" id="scenarios">
          <h2>{landingCopy.scenariosTitle}</h2>
          <ul className="landing-scenarios">
            {landingScenarios.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-section" id="deploy">
          <header className="landing-section-head">
            <h2>{landingCopy.deployTitle}</h2>
            <p className="landing-lead">{landingCopy.deployLead}</p>
          </header>
          <ul className="landing-deploy">
            {landingDeploy.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="landing-waitlist" id="waitlist">
          <div>
            <h2>{landingCopy.waitlistTitle}</h2>
            <p className="landing-lead">{landingCopy.waitlistLead}</p>
          </div>
          <WaitlistForm />
        </section>
      </main>

      <footer className="landing-footer">
        <span>{landingCopy.brand}</span>
        <p>{landingCopy.footerNote}</p>
        <Link href="/login">{landingCopy.login}</Link>
      </footer>
    </div>
  );
}
